import { observer } from "mobx-react";
import { TMessage } from "../i18n/tt";
import { IconName } from "./iconMapping";
import { FgColor } from "./colors";
import { useTranslation } from "../stores/RootStoreContext";
import { classNames } from "../utils/classNames";
import styles from "./MenuBar.module.css";
import { Box } from "./Box";
import { Icon } from "./Icon";
import { Tag } from "./Tag";
import { BetaLabel } from "./BetaLabel";
import { useEffect, useRef } from "react";
import { debounce } from "lodash";

export type MenuLinkProps = {
  url?: string;
  text: TMessage;
  icon: IconName;
  badge?: TMessage;
  badgeColor?: FgColor;
  isActive?: boolean;
  tag?: TMessage;
  onActivate?: () => void;
};

export const MenuItem = observer(function MenuLink({
  url,
  text,
  icon,
  badge,
  badgeColor,
  isActive,
  tag,
  onActivate,
}: MenuLinkProps) {
  const { t } = useTranslation();
  const className = classNames(
    "tw-p-2",
    "tw-rounded-lg",
    "tw-no-underline",
    "color-default",
    "active:color-default",
    "visited:color-default",
    "hover:color-default",
    "hover:bg-alt1",
    isActive && "tw-font-bold bg-alt2-dimmed",
  );
  const content = (
    <Box alignItems={"center"} gap={"sm"} whitespace={"nowrap"}>
      <Icon className={styles.linkIcon} name={icon} color={isActive ? "info" : "default"} />
      <span className={styles.linkText}>{t(text)}</span>
      {tag && <Tag compact label={t(tag)} color={"error"} />}
      {badge && <BetaLabel label={badge} color={badgeColor} />}
    </Box>
  );

  if (url) {
    return (
      <a href={url} className={className} data-collapsed-tooltip={t(text)}>
        {content}
      </a>
    );
  }
  return (
    <button className={className} data-collapsed-tooltip={t(text)} onClick={onActivate}>
      {content}
    </button>
  );
});

function updateMenuSize(container: HTMLDivElement) {
  const items = Array.from(container.querySelectorAll<HTMLElement>("a, button"));

  if (container.scrollWidth > container.offsetWidth) {
    for (const link of items.toReversed()) {
      link.classList.add(styles.collapsed);
      if (container.scrollWidth <= container.offsetWidth) {
        break;
      }
    }
  } else {
    for (const item of items) {
      item.classList.remove(styles.collapsed);
      if (container.scrollWidth > container.offsetWidth) {
        item.classList.add(styles.collapsed);
        break;
      }
    }
  }

  for (const item of items) {
    if (item.classList.contains(styles.collapsed)) {
      item.dataset.tooltip = item.dataset.collapsedTooltip;
    } else {
      delete item.dataset.tooltip;
    }
  }
}

export type MenuBarProps = {
  items: MenuLinkProps[];
  containerRef: HTMLElement | null;
};

export const MenuBar = observer(({ items, containerRef }: MenuBarProps) => {
  const { t } = useTranslation();
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef;
    const itemsContainer = itemsContainerRef.current;
    if (!container || !itemsContainer) {
      return;
    }
    const o = new ResizeObserver(debounce(() => updateMenuSize(itemsContainer), 100));
    o.observe(container);
    return () => {
      o.disconnect();
    };
  }, [containerRef]);

  return (
    <Box
      overflow={"hidden"}
      alignItems={"center"}
      ref={itemsContainerRef}
      paddingX={"sm"}
      className={"tw-grow-[1000] tw-shrink"}
    >
      {items.map((item) => (
        <MenuItem key={t(item.text)} {...item} />
      ))}
    </Box>
  );
});
